import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import EventTriggeredCurriculum from "./EventTriggeredCurriculum";
import LocationSettings from "./LocationSettings";
import InvisibleTeacher from "./InvisibleTeacher";
import AnimatedHelper from "./AnimatedHelper";
import StandardLessonActions from "./StandardLessonActions";
import { clearLessonProgress } from "../utils/lessonProgress";
import NameAndLineageModule from "./NameAndLineageModule";
import { isYoungLearnerGrade } from "../utils/gradeUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tier color schemes
const TIER_COLORS = {
  tier1: {
    primary: "#FFD700",
    secondary: "#FFA500",
    bg: "linear-gradient(135deg, #1a1a0a 0%, #2a2a1a 100%)",
    accent: "#FFE55C"
  },
  tier2: {
    primary: "#4169E1",
    secondary: "#1E90FF",
    bg: "linear-gradient(135deg, #0a0a1a 0%, #1a1a2a 100%)",
    accent: "#87CEEB"
  },
  tier3: {
    primary: "#8B0000",
    secondary: "#DC143C",
    bg: "linear-gradient(135deg, #1a0a0a 0%, #2a1a1a 100%)",
    accent: "#FF6B6B"
  }
};

function SpecialEdition({ user, gradeLevel, autoOpenForensicEtymology = false }) {
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [tierSubjects, setTierSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [curriculum, setCurriculum] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [lessonContent, setLessonContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingLesson, setLoadingLesson] = useState(false);
  const [error, setError] = useState("");
  const [certifications, setCertifications] = useState([]);
  const [showTranscript, setShowTranscript] = useState(false);
  const [transcript, setTranscript] = useState(null);
  // NEW: View mode toggle - "living" (event-triggered) or "archive" (traditional)
  const [viewMode, setViewMode] = useState("living");
  const [showLocationSettings, setShowLocationSettings] = useState(false);
  // 2026-02-11d: standalone module open flag — when true, renders
  // the "The Name and the Lineage" curriculum instead of the
  // tier/subject browser.
  const [showNameAndLineage, setShowNameAndLineage] = useState(false);

  // Restart key bumped by 🔄 Restart Lesson to force the lesson body
  // to re-render fresh + remount the InvisibleTeacher.
  const [seLessonRestartKey, setSeLessonRestartKey] = useState(0);
  const [sePdfLoading, setSePdfLoading] = useState(false);
  const seLessonBodyRef = useRef(null);

  // 5-button action bar handlers shared across all Special Edition
  // lessons (Tier 1 / Tier 2 / Tier 3 — Forensic Etymology, Zion of
  // Pillars, all modules under any tier).
  const handleSeRestartLesson = useCallback(() => {
    clearLessonProgress(lessonContent?.lesson_title);
    setSeLessonRestartKey((k) => k + 1);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [lessonContent]);

  const handleSeTakeQuiz = useCallback(async () => {
    if (!selectedLesson || !selectedSubject) return;
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/special-edition/generate-quiz`,
        null,
        {
          params: {
            subject_id: selectedSubject.id,
            module_id: selectedLesson.moduleId,
            lesson_number: selectedLesson.lessonNumber,
            grade_level: gradeLevel,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      // Surface the quiz inline as an alert prompt for now — the
      // Special Edition quiz UI is a future task. This keeps the
      // button functional and unifies UX across the platform.
      const q = res.data?.questions?.[0]?.question || "Quiz generated. Quiz UI coming soon.";
      alert(`📝 Lesson Quiz Ready\n\n${q}`);
    } catch {
      alert("Quiz unavailable for this lesson — please try again shortly.");
    }
  }, [selectedLesson, selectedSubject, gradeLevel]);

  // Compute next sequential lesson within the current module.
  const seNextLesson = useMemo(() => {
    if (!curriculum || !selectedLesson) return null;
    const mod = (curriculum.modules || []).find((m) => m.id === selectedLesson.moduleId);
    if (!mod) return null;
    const lessons = mod.lessons_detail || [];
    const idx = lessons.findIndex((l) => l.number === selectedLesson.lessonNumber);
    if (idx === -1 || idx >= lessons.length - 1) return null;
    return { ...lessons[idx + 1], moduleId: mod.id };
  }, [curriculum, selectedLesson]);

  const handleSeNextLesson = useCallback(() => {
    if (!seNextLesson) return;
    fetchLessonContent(seNextLesson.moduleId, seNextLesson.number);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seNextLesson]);

  const handleSeDownloadPDF = useCallback(async () => {
    if (!lessonContent) return;
    setSePdfLoading(true);
    try {
      const { exportLessonPDF } = await import("../utils/pdfExport");
      await exportLessonPDF({
        title: lessonContent.lesson_title || "Special Edition Lesson",
        subtitle: `Special Edition · ${selectedSubject?.name || "Lesson"} · Grade ${gradeLevel}`,
        bodyNode: seLessonBodyRef.current,
        studentName: user?.full_name || user?.username,
        gradeLevel,
      });
    } catch (e) {
      console.error("PDF export failed:", e);
      alert("PDF export failed. Please try again.");
    } finally {
      setSePdfLoading(false);
    }
  }, [lessonContent, selectedSubject, gradeLevel, user]);

  // Direct access to Forensic Etymology curriculum
  const openForensicEtymology = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      // Fetch tier1 subjects first
      const tierResponse = await axios.get(`${API}/special-edition/tier/tier1/subjects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTierSubjects(tierResponse.data.subjects);
      setSelectedTier({ id: "tier1", ...tierResponse.data.tier });
      
      // Find and select Forensic Etymology
      const forensicSubject = tierResponse.data.subjects.find(s => s.id === "forensic-etymology");
      if (forensicSubject) {
        // Fetch its curriculum
        const currResponse = await axios.get(
          `${API}/special-edition/subject/forensic-etymology/curriculum`,
          {
            params: { grade_level: gradeLevel },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setCurriculum(currResponse.data);
        setSelectedSubject(currResponse.data.subject);
      }
    } catch (err) {
      console.error("Error loading Forensic Etymology:", err);
      setError("Failed to load Forensic Etymology curriculum");
    } finally {
      setLoading(false);
    }
  };

  // Fetch tiers on mount, and auto-open Forensic Etymology if requested
  useEffect(() => {
    fetchTiers();
    fetchCertifications();
    
    // Auto-open Forensic Etymology if prop is set
    if (autoOpenForensicEtymology) {
      openForensicEtymology();
    }
  }, [autoOpenForensicEtymology]);

  const fetchTiers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/special-edition/tiers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTiers(response.data.tiers);
    } catch (err) {
      console.error("Error fetching tiers:", err);
      setError("Failed to load Special Edition content");
    } finally {
      setLoading(false);
    }
  };

  const fetchCertifications = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/special-edition/certifications/available`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCertifications(response.data.certifications || []);
    } catch (err) {
      console.error("Error fetching certifications:", err);
    }
  };

  const fetchTierSubjects = async (tierId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/special-edition/tier/${tierId}/subjects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTierSubjects(response.data.subjects);
      setSelectedTier({ id: tierId, ...response.data.tier });
    } catch (err) {
      console.error("Error fetching tier subjects:", err);
      if (err.response?.status === 403) {
        setError("This tier requires adult verification or ordination candidate status.");
      } else {
        setError("Failed to load tier content");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjectCurriculum = async (subjectId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/special-edition/subject/${subjectId}/curriculum`,
        {
          params: { grade_level: gradeLevel },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setCurriculum(response.data);
      setSelectedSubject(response.data.subject);
    } catch (err) {
      console.error("Error fetching curriculum:", err);
      setError("Failed to load curriculum");
    } finally {
      setLoading(false);
    }
  };

  const fetchLessonContent = async (moduleId, lessonNumber) => {
    try {
      setLoadingLesson(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/special-edition/generate-lesson`,
        null,
        {
          params: {
            subject_id: selectedSubject.id,
            module_id: moduleId,
            lesson_number: lessonNumber,
            grade_level: gradeLevel
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setLessonContent(response.data);
      setSelectedLesson({ moduleId, lessonNumber });
    } catch (err) {
      console.error("Error fetching lesson:", err);
      setError("Failed to load lesson content");
    } finally {
      setLoadingLesson(false);
    }
  };

  const fetchTranscript = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/special-edition/compliance/transcript/${user.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTranscript(response.data);
      setShowTranscript(true);
    } catch (err) {
      console.error("Error fetching transcript:", err);
    }
  };

  const goBack = () => {
    if (lessonContent) {
      setLessonContent(null);
      setSelectedLesson(null);
    } else if (curriculum) {
      setCurriculum(null);
      setSelectedSubject(null);
    } else if (selectedTier) {
      setSelectedTier(null);
      setTierSubjects([]);
    }
    setError("");
  };

  // Render loading state
  if (loading && !selectedTier && tiers.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px" }}>
        <div style={{ fontSize: "3rem", marginBottom: "20px" }}>🌟</div>
        <p style={{ color: "#FFD700", fontSize: "1.2rem" }}>Loading Special Edition...</p>
      </div>
    );
  }

  // Render lesson content
  if (lessonContent) {
    const colors = TIER_COLORS[selectedTier?.id] || TIER_COLORS.tier1;
    const content = lessonContent.content || {};
    
    return (
      <div data-testid="special-edition-lesson" style={{ maxWidth: "1000px", margin: "0 auto" }}>
        <div ref={seLessonBodyRef} key={seLessonRestartKey}>
        {/* Header */}
        <div className="card" style={{ background: colors.bg, borderTop: `4px solid ${colors.primary}` }}>
          <button
            onClick={goBack}
            style={{ background: "transparent", border: "none", color: "#aaa", cursor: "pointer", marginBottom: "15px" }}
          >
            ← Back to Curriculum
          </button>
          <h2 style={{ color: colors.primary, margin: "0 0 10px 0" }}>
            {lessonContent.lesson_title}
          </h2>
          <p style={{ color: "#888", margin: 0 }}>
            {lessonContent.academic_title} | {gradeLevel} Level
          </p>
          <div style={{ 
            marginTop: "15px", 
            padding: "10px 15px", 
            background: "rgba(255,255,255,0.05)", 
            borderRadius: "8px",
            fontSize: "0.9rem",
            color: "#aaa"
          }}>
            <strong style={{ color: colors.accent }}>Depth Level:</strong> {lessonContent.depth_level?.description}
          </div>
        </div>

        {/* 2026-02-21 YLC Motion Studio — cartoon helper banner for
            every Special Edition lesson. Cache-first: instant on
            repeat visits, ~15-30s cold render. Zero API cost. */}
        {content.introduction && (
          <AnimatedHelper
            lessonId={`se-${selectedSubject?.id || selectedSubject}-${gradeLevel}`}
            prompt={`${lessonContent.academic_title || lessonContent.lesson_title || selectedSubject?.name || "Lesson"} — Special Edition`}
            gradeTier={
              (gradeLevel || "").toLowerCase().includes("seminary") ||
              (gradeLevel || "").toLowerCase().includes("adult") ||
              (gradeLevel || "").toLowerCase().includes("ordination")
                ? "Seminary"
                : (["9-12", "9", "10", "11", "12"].includes(String(gradeLevel).toLowerCase()) ? "9-12" : "6-8")
            }
            narrationText={(content.introduction || "").slice(0, 900)}
            lessonTitle={lessonContent.lesson_title || "Special Edition"}
            variant="banner"
          />
        )}

        {/* Introduction */}
        {content.introduction && (
          <div className="card">
            <h3 style={{ color: colors.primary }}>Introduction</h3>
            <div style={{ lineHeight: "1.8", color: "#ddd" }}>
              <ReactMarkdown>{content.introduction}</ReactMarkdown>
            </div>
          </div>
        )}

        {/* Main Sections */}
        {content.sections?.map((section, idx) => (
          <div key={idx} className="card">
            <h3 style={{ color: colors.accent }}>{section.title}</h3>
            <div style={{ lineHeight: "1.8", color: "#ddd" }}>
              <ReactMarkdown>{section.content}</ReactMarkdown>
            </div>
          </div>
        ))}

        {/* Scripture References */}
        {content.scripture_references?.length > 0 && (
          <div className="card" style={{ background: "#1a2a1a", borderLeft: `4px solid ${colors.primary}` }}>
            <h3 style={{ color: "#90EE90" }}>Scripture References</h3>
            {content.scripture_references.map((ref, idx) => (
              <div key={idx} style={{ marginBottom: "15px", padding: "10px", background: "rgba(0,0,0,0.2)", borderRadius: "8px" }}>
                <strong style={{ color: colors.primary }}>{ref.reference}</strong>
                <p style={{ color: "#ccc", margin: "5px 0 0 0" }}>{ref.explanation}</p>
              </div>
            ))}
          </div>
        )}

        {/* Primary Sources */}
        {content.primary_sources?.length > 0 && (
          <div className="card" style={{ background: "#1a1a2a", borderLeft: `4px solid ${colors.secondary}` }}>
            <h3 style={{ color: colors.accent }}>Primary Sources & Historical Evidence</h3>
            {content.primary_sources.map((source, idx) => (
              <div key={idx} style={{ marginBottom: "15px", padding: "10px", background: "rgba(0,0,0,0.2)", borderRadius: "8px" }}>
                <strong style={{ color: colors.secondary }}>{source.source}</strong>
                <p style={{ color: "#ccc", margin: "5px 0 0 0" }}>{source.description}</p>
              </div>
            ))}
          </div>
        )}

        {/* Vocabulary */}
        {content.vocabulary?.length > 0 && (
          <div className="card">
            <h3 style={{ color: colors.primary }}>Key Vocabulary</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: "15px" }}>
              {content.vocabulary.map((item, idx) => (
                <div key={idx} style={{ padding: "12px", background: "rgba(255,255,255,0.05)", borderRadius: "8px" }}>
                  <strong style={{ color: colors.accent }}>{item.term}</strong>
                  <p style={{ color: "#aaa", margin: "5px 0 0 0", fontSize: "0.9rem" }}>{item.definition}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Discussion Questions */}
        {content.discussion_questions?.length > 0 && (
          <div className="card" style={{ background: "#2a1a2a", borderLeft: `4px solid ${colors.primary}` }}>
            <h3 style={{ color: "#DDA0DD" }}>Discussion Questions</h3>
            <ol style={{ color: "#ddd", lineHeight: "2" }}>
              {content.discussion_questions.map((q, idx) => (
                <li key={idx} style={{ marginBottom: "10px" }}>{q}</li>
              ))}
            </ol>
          </div>
        )}

        {/* Summary */}
        {content.summary && (
          <div className="card" style={{ background: colors.bg, borderTop: `4px solid ${colors.primary}` }}>
            <h3 style={{ color: colors.primary }}>Summary</h3>
            <div style={{ lineHeight: "1.8", color: "#ddd" }}>
              <ReactMarkdown>{content.summary}</ReactMarkdown>
            </div>
          </div>
        )}
        
        {/* THE INVISIBLE TEACHER - Voice-First AI for Special Edition */}
        <InvisibleTeacher
          key={`teacher-${seLessonRestartKey}`}
          gradeLevel={gradeLevel}
          lessonContent={[
            content.introduction || '',
            ...(content.sections?.map(s => `${s.title}\n\n${s.content}`) || []),
            content.summary || ''
          ].filter(Boolean).join('\n\n')}
          lessonTitle={lessonContent.lesson_title}
          user={user}
          autoStart={isYoungLearnerGrade(gradeLevel)}
          // 2026-02-11c: AI ↔ visible sync — broadcasts section
          // index so the Special Edition lesson view can highlight
          // / scroll to whatever the AI is currently reading.
          onSectionStart={(idx) => {
            try {
              window.dispatchEvent(
                new CustomEvent("ylc:teacher-section-start", { detail: { idx } })
              );
            } catch {
              /* non-fatal */
            }
          }}
        />
        </div>

        {/* Standardized 5-button lesson action bar — identical across
            K-12 Academic, Courses, and Special Edition (Zion of Pillars,
            Forensic Etymology, every tier). User mandate 2026-02. */}
        <StandardLessonActions
          onBack={goBack}
          onRestart={handleSeRestartLesson}
          onTakeQuiz={handleSeTakeQuiz}
          onNextLesson={seNextLesson ? handleSeNextLesson : undefined}
          nextLessonLabel={seNextLesson?.title}
          onDownloadPDF={handleSeDownloadPDF}
          pdfLoading={sePdfLoading}
        />
      </div>
    );
  }

  // Render curriculum view
  if (curriculum && selectedSubject) {
    const colors = TIER_COLORS[selectedTier?.id] || TIER_COLORS.tier1;
    
    return (
      <div data-testid="special-edition-curriculum" style={{ maxWidth: "1000px", margin: "0 auto" }}>
        {/* Header */}
        <div className="card" style={{ background: colors.bg, borderTop: `4px solid ${colors.primary}` }}>
          <button
            onClick={goBack}
            style={{ background: "transparent", border: "none", color: "#aaa", cursor: "pointer", marginBottom: "15px" }}
          >
            ← Back to Subjects
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <span style={{ fontSize: "3rem" }}>{selectedSubject.icon}</span>
            <div>
              <h2 style={{ color: colors.primary, margin: 0 }}>{selectedSubject.name}</h2>
              <p style={{ color: colors.accent, margin: "5px 0" }}>{selectedSubject.subtitle}</p>
            </div>
          </div>
          <div style={{ 
            marginTop: "20px", 
            padding: "15px", 
            background: "rgba(255,255,255,0.05)", 
            borderRadius: "8px"
          }}>
            <div style={{ color: "#aaa", fontSize: "0.9rem", marginBottom: "10px" }}>
              <strong style={{ color: colors.accent }}>Academic Label:</strong> {selectedSubject.academic_label}
            </div>
            <div style={{ color: "#aaa", fontSize: "0.9rem" }}>
              <strong style={{ color: colors.accent }}>Credit Type:</strong> {selectedSubject.credit_type}
            </div>
          </div>
        </div>

        {/* Modules */}
        {curriculum.modules?.map((module, idx) => (
          <div key={idx} className="card" style={{ borderLeft: `4px solid ${colors.secondary}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "15px" }}>
              <div>
                <h3 style={{ color: colors.accent, margin: 0 }}>{module.title}</h3>
                <p style={{ color: "#888", fontSize: "0.9rem", margin: "5px 0" }}>{module.academic_title}</p>
              </div>
              <span style={{ 
                background: colors.primary, 
                color: "#000", 
                padding: "4px 12px", 
                borderRadius: "12px",
                fontSize: "0.85rem",
                fontWeight: "bold"
              }}>
                {module.lesson_count} Lessons
              </span>
            </div>
            
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {module.lessons_detail?.map((lesson, lessonIdx) => (
                <button
                  key={lessonIdx}
                  onClick={() => fetchLessonContent(module.id, lesson.number)}
                  disabled={loadingLesson}
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    border: `1px solid ${colors.secondary}40`,
                    borderRadius: "8px",
                    padding: "12px 15px",
                    color: "#ddd",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.2s"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <span style={{ 
                      background: colors.primary, 
                      color: "#000", 
                      width: "28px", 
                      height: "28px", 
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.85rem",
                      fontWeight: "bold"
                    }}>
                      {lesson.number}
                    </span>
                    <div>
                      <div style={{ fontWeight: "500" }}>{lesson.title}</div>
                      <div style={{ fontSize: "0.8rem", color: "#888", marginTop: "2px" }}>
                        {lesson.depth_note}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}

        {loadingLesson && (
          <div className="card" style={{ textAlign: "center", padding: "40px" }}>
            <div style={{ fontSize: "2rem", marginBottom: "15px" }}>📖</div>
            <p style={{ color: colors.primary }}>Generating lesson content...</p>
            <p style={{ color: "#888", fontSize: "0.9rem" }}>Preparing age-appropriate material for {gradeLevel} level</p>
          </div>
        )}
      </div>
    );
  }

  // Render tier subjects
  if (selectedTier && tierSubjects.length > 0) {
    const colors = TIER_COLORS[selectedTier.id] || TIER_COLORS.tier1;
    
    return (
      <div data-testid="special-edition-subjects" style={{ maxWidth: "1000px", margin: "0 auto" }}>
        {/* Header */}
        <div className="card" style={{ background: colors.bg, borderTop: `4px solid ${colors.primary}` }}>
          <button
            onClick={goBack}
            style={{ background: "transparent", border: "none", color: "#aaa", cursor: "pointer", marginBottom: "15px" }}
          >
            ← Back to Tiers
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <span style={{ fontSize: "3rem" }}>{selectedTier.icon}</span>
            <div>
              <h2 style={{ color: colors.primary, margin: 0 }}>{selectedTier.name}</h2>
              <p style={{ color: "#aaa", margin: "5px 0" }}>{selectedTier.description}</p>
            </div>
          </div>
        </div>

        {/* Subjects Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "20px" }}>
          {tierSubjects.map((subject) => (
            <div
              key={subject.id}
              className="card"
              onClick={() => fetchSubjectCurriculum(subject.id)}
              style={{
                cursor: "pointer",
                borderLeft: `4px solid ${colors.secondary}`,
                transition: "transform 0.2s, box-shadow 0.2s"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "15px" }}>
                <span style={{ fontSize: "2rem" }}>{subject.icon}</span>
                <div>
                  <h3 style={{ color: colors.accent, margin: 0 }}>{subject.name}</h3>
                  <p style={{ color: "#888", fontSize: "0.85rem", margin: "3px 0 0 0" }}>{subject.subtitle}</p>
                </div>
              </div>
              <p style={{ color: "#aaa", fontSize: "0.9rem", lineHeight: "1.6" }}>{subject.description}</p>
              <div style={{ 
                marginTop: "15px", 
                padding: "8px 12px", 
                background: "rgba(255,255,255,0.05)", 
                borderRadius: "6px",
                fontSize: "0.8rem",
                color: "#888"
              }}>
                <strong style={{ color: colors.accent }}>Credit:</strong> {subject.credit_type}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Render Name & Lineage standalone module (sits alongside Forensic Etymology)
  if (showNameAndLineage) {
    return (
      <NameAndLineageModule
        onBack={() => setShowNameAndLineage(false)}
        user={user}
        gradeLevel={gradeLevel}
      />
    );
  }

  // Render main tier selection
  return (
    <div data-testid="special-edition-main" style={{ maxWidth: "1200px", margin: "0 auto" }}>
      {/* Hero Header */}
      <div className="card" style={{ 
        background: "linear-gradient(135deg, #1a1a2a 0%, #2a1a1a 50%, #1a2a1a 100%)",
        borderTop: "4px solid #FFD700",
        textAlign: "center",
        padding: "40px 30px"
      }}>
        <h1 style={{ 
          color: "#FFD700", 
          margin: "0 0 15px 0",
          fontSize: "2.2rem",
          textShadow: "0 2px 10px rgba(255,215,0,0.3)"
        }}>
          Special Edition: Restoration Track
        </h1>
        <p style={{ color: "#aaa", fontSize: "1.1rem", maxWidth: "700px", margin: "0 auto", lineHeight: "1.7" }}>
          Discover the hidden truth of Yisrael through Science, History, and Sacred Studies. 
          A groundbreaking enhancement to your K-12 journey.
        </p>
        
        {/* View Mode Toggle */}
        <div style={{ 
          marginTop: "25px", 
          display: "flex", 
          gap: "0", 
          justifyContent: "center",
          background: "#0a0a0a",
          borderRadius: "10px",
          padding: "4px",
          maxWidth: "450px",
          margin: "25px auto 0"
        }}>
          <button
            data-testid="view-mode-living"
            onClick={() => setViewMode("living")}
            style={{
              flex: 1,
              padding: "12px 20px",
              background: viewMode === "living" 
                ? "linear-gradient(135deg, #90EE90 0%, #32CD32 100%)" 
                : "transparent",
              border: "none",
              borderRadius: "8px",
              color: viewMode === "living" ? "#000" : "#888",
              fontWeight: viewMode === "living" ? "bold" : "normal",
              cursor: "pointer",
              transition: "all 0.3s"
            }}
          >
            📜 Living School
          </button>
          <button
            data-testid="view-mode-archive"
            onClick={() => setViewMode("archive")}
            style={{
              flex: 1,
              padding: "12px 20px",
              background: viewMode === "archive" 
                ? "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)" 
                : "transparent",
              border: "none",
              borderRadius: "8px",
              color: viewMode === "archive" ? "#000" : "#888",
              fontWeight: viewMode === "archive" ? "bold" : "normal",
              cursor: "pointer",
              transition: "all 0.3s"
            }}
          >
            📚 Full Archive
          </button>
        </div>
        
        <p style={{ 
          color: "#666", 
          fontSize: "0.8rem", 
          marginTop: "10px",
          maxWidth: "500px",
          margin: "10px auto 0"
        }}>
          {viewMode === "living" 
            ? "Event-triggered lessons synchronized with the Creator's Clock" 
            : "Browse all curriculum content by tier and subject"}
        </p>
        
        {/* Quick Actions */}
        <div style={{ marginTop: "20px", display: "flex", gap: "15px", justifyContent: "center", flexWrap: "wrap" }}>
          <button
            onClick={() => setShowLocationSettings(!showLocationSettings)}
            style={{
              background: "rgba(144,238,144,0.1)",
              border: "1px solid #90EE90",
              color: "#90EE90",
              padding: "10px 20px",
              borderRadius: "8px",
              cursor: "pointer"
            }}
          >
            📍 Location Settings
          </button>
          <button
            onClick={fetchTranscript}
            style={{
              background: "rgba(255,255,255,0.1)",
              border: "1px solid #FFD700",
              color: "#FFD700",
              padding: "10px 20px",
              borderRadius: "8px",
              cursor: "pointer"
            }}
          >
            View Transcript
          </button>
          {certifications.length > 0 && (
            <button
              style={{
                background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
                border: "none",
                color: "#000",
                padding: "10px 20px",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "bold"
              }}
            >
              {certifications.length} Certificate(s) Available
            </button>
          )}
        </div>
      </div>

      {/* Location Settings Panel */}
      {showLocationSettings && (
        <div style={{ marginTop: "20px" }}>
          <LocationSettings 
            onLocationUpdate={() => setShowLocationSettings(false)}
          />
        </div>
      )}

      {error && (
        <div className="card" style={{ background: "#4d1a1a", borderLeft: "4px solid #ff6b6b" }}>
          <p style={{ color: "#ff6b6b", margin: 0 }}>{error}</p>
        </div>
      )}

      {/* FORENSIC ETYMOLOGY - Prominent Quick Access Card */}
      {!selectedSubject && !curriculum && (
        <div 
          data-testid="forensic-etymology-quick-access"
          onClick={openForensicEtymology}
          className="card"
          style={{
            background: "linear-gradient(135deg, #2a2a1a 0%, #1a1a0a 100%)",
            borderTop: "4px solid #FFD700",
            borderLeft: "4px solid #FFD700",
            cursor: "pointer",
            marginTop: "20px",
            padding: "25px",
            transition: "transform 0.2s, box-shadow 0.2s"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-3px)";
            e.currentTarget.style.boxShadow = "0 8px 25px rgba(255, 215, 0, 0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
              <span style={{ fontSize: "3.5rem" }}>📜</span>
              <div>
                <div style={{ 
                  color: "#FFD700", 
                  fontWeight: "bold", 
                  fontSize: "1.5rem",
                  marginBottom: "8px"
                }}>
                  Forensic Etymology
                </div>
                <div style={{ color: "#FFA500", fontSize: "1rem", marginBottom: "5px" }}>
                  Stripping Roman Paint to Find Hebrew Copper
                </div>
                <div style={{ color: "#aaa", fontSize: "0.9rem", maxWidth: "500px" }}>
                  Strip away Roman/Greek "Transliterations" to reveal the functional Hebrew "Parent Roots". 
                  Every word traced to its 2-letter mechanical blueprint.
                </div>
              </div>
            </div>
            <div style={{
              background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
              color: "#000",
              padding: "15px 30px",
              borderRadius: "25px",
              fontSize: "1rem",
              fontWeight: "bold",
              whiteSpace: "nowrap"
            }}>
              Start Learning →
            </div>
          </div>
          <div style={{ 
            marginTop: "15px", 
            paddingTop: "15px", 
            borderTop: "1px solid rgba(255,215,0,0.2)",
            display: "flex",
            gap: "20px",
            flexWrap: "wrap"
          }}>
            <span style={{ color: "#888", fontSize: "0.85rem" }}>
              📚 K-12+ Curriculum
            </span>
            <span style={{ color: "#888", fontSize: "0.85rem" }}>
              ✏️ 52 Lessons
            </span>
            <span style={{ color: "#888", fontSize: "0.85rem" }}>
              🎓 Language Arts Elective Credit
            </span>
          </div>
        </div>
      )}

      {/* THE NAME AND THE LINEAGE - Standalone Module (alongside Forensic Etymology)
          2026-02-11d user mandate: "add this curriculum as a standalone module
          to sit in the Special Edition alongside the Forensic Etymology section." */}
      {!selectedSubject && !curriculum && (
        <div
          data-testid="name-and-lineage-quick-access"
          onClick={() => setShowNameAndLineage(true)}
          className="card"
          style={{
            background: "linear-gradient(135deg, #2a1a3a 0%, #1a0a2a 100%)",
            borderTop: "4px solid #DDA0DD",
            borderLeft: "4px solid #DDA0DD",
            cursor: "pointer",
            marginTop: "20px",
            padding: "25px",
            transition: "transform 0.2s, box-shadow 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-3px)";
            e.currentTarget.style.boxShadow = "0 8px 25px rgba(221, 160, 221, 0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "20px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
              <span style={{ fontSize: "3.5rem" }}>🕎</span>
              <div>
                <div
                  style={{
                    color: "#DDA0DD",
                    fontWeight: "bold",
                    fontSize: "1.5rem",
                    marginBottom: "8px",
                  }}
                >
                  The Name and the Lineage
                </div>
                <div style={{ color: "#FFA500", fontSize: "1rem", marginBottom: "5px" }}>
                  Aahaya · The Four Letters · The Corporate Son · Lifting the Veil
                </div>
                <div style={{ color: "#aaa", fontSize: "0.9rem", maxWidth: "560px" }}>
                  Four-unit standalone curriculum restoring Hebrew identity:
                  the I AM, the YHWH blueprint, the Almah mistranslation, the
                  corporate sonship of Yisrael, and the Council of Nicaea veil.
                </div>
              </div>
            </div>
            <div
              style={{
                background: "linear-gradient(135deg, #DDA0DD 0%, #BA55D3 100%)",
                color: "#000",
                padding: "15px 30px",
                borderRadius: "25px",
                fontSize: "1rem",
                fontWeight: "bold",
                whiteSpace: "nowrap",
              }}
            >
              Begin Module →
            </div>
          </div>
          <div
            style={{
              marginTop: "15px",
              paddingTop: "15px",
              borderTop: "1px solid rgba(221,160,221,0.2)",
              display: "flex",
              gap: "20px",
              flexWrap: "wrap",
            }}
          >
            <span style={{ color: "#888", fontSize: "0.85rem" }}>📚 4 Units</span>
            <span style={{ color: "#888", fontSize: "0.85rem" }}>👶 Youth + 🎓 Adult Strategies</span>
            <span style={{ color: "#888", fontSize: "0.85rem" }}>🎧 InvisibleTeacher</span>
            <span style={{ color: "#888", fontSize: "0.85rem" }}>📄 Downloadable PDF</span>
          </div>
        </div>
      )}

      {/* Living School Mode - Event-Triggered Curriculum */}
      {viewMode === "living" && (
        <div style={{ marginTop: "25px" }}>
          <EventTriggeredCurriculum 
            gradeLevel={gradeLevel}
            onSelectLesson={(lesson) => console.log("Selected lesson:", lesson)}
            onBack={() => setViewMode("archive")}
            backLabel="← Special Edition Home"
          />
        </div>
      )}

      {/* Archive Mode - Traditional Tier Cards */}
      {viewMode === "archive" && (
        <>
          {/* Tier Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))", gap: "25px", marginTop: "20px" }}>
        {tiers.map((tier) => {
          const colors = TIER_COLORS[tier.id] || TIER_COLORS.tier1;
          
          return (
            <div
              key={tier.id}
              className="card"
              onClick={() => !tier.locked && fetchTierSubjects(tier.id)}
              style={{
                background: colors.bg,
                borderTop: `4px solid ${tier.locked ? "#666" : colors.primary}`,
                cursor: tier.locked ? "not-allowed" : "pointer",
                opacity: tier.locked ? 0.7 : 1,
                transition: "transform 0.2s, box-shadow 0.2s",
                position: "relative"
              }}
            >
              {tier.locked && (
                <div style={{
                  position: "absolute",
                  top: "15px",
                  right: "15px",
                  background: "#333",
                  padding: "5px 10px",
                  borderRadius: "4px",
                  fontSize: "0.8rem",
                  color: "#ff6b6b"
                }}>
                  🔒 Locked
                </div>
              )}
              
              <div style={{ display: "flex", alignItems: "center", gap: "15px", marginBottom: "20px" }}>
                <span style={{ 
                  fontSize: "3rem",
                  filter: tier.locked ? "grayscale(1)" : "none"
                }}>
                  {tier.icon}
                </span>
                <div>
                  <h2 style={{ color: tier.locked ? "#666" : colors.primary, margin: 0 }}>{tier.name}</h2>
                  <span style={{ 
                    background: tier.locked ? "#333" : colors.primary,
                    color: tier.locked ? "#666" : "#000",
                    padding: "3px 10px",
                    borderRadius: "10px",
                    fontSize: "0.8rem",
                    fontWeight: "bold"
                  }}>
                    {tier.subject_count} Subjects
                  </span>
                </div>
              </div>
              
              <p style={{ color: tier.locked ? "#555" : "#aaa", lineHeight: "1.7" }}>
                {tier.description}
              </p>
              
              {tier.locked && tier.lock_reason && (
                <p style={{ color: "#ff6b6b", fontSize: "0.85rem", marginTop: "15px" }}>
                  {tier.lock_reason}
                </p>
              )}
              
              {!tier.locked && (
                <button
                  style={{
                    marginTop: "15px",
                    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                    border: "none",
                    color: "#000",
                    padding: "12px 25px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "bold",
                    width: "100%"
                  }}
                >
                  Explore {tier.name} →
                </button>
              )}
            </div>
          );
        })}
      </div>
        </>
      )}

      {/* Transcript Modal */}
      {showTranscript && transcript && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.8)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999
        }}
        onClick={() => setShowTranscript(false)}
        >
          <div 
            className="card" 
            style={{ maxWidth: "700px", maxHeight: "80vh", overflow: "auto" }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ color: "#FFD700", margin: 0 }}>Special Edition Transcript</h2>
              <button
                onClick={() => setShowTranscript(false)}
                style={{ background: "transparent", border: "none", color: "#aaa", fontSize: "1.5rem", cursor: "pointer" }}
              >
                ×
              </button>
            </div>
            
            <div style={{ marginBottom: "20px", padding: "15px", background: "rgba(255,215,0,0.1)", borderRadius: "8px" }}>
              <p style={{ margin: "0 0 5px 0", color: "#FFD700" }}><strong>Student:</strong> {transcript.transcript.student_name}</p>
              <p style={{ margin: "0 0 5px 0", color: "#aaa" }}><strong>Grade Level:</strong> {transcript.transcript.grade_level}</p>
              <p style={{ margin: "0", color: "#aaa" }}><strong>Total Credits:</strong> {transcript.transcript.total_credits}</p>
            </div>
            
            <p style={{ color: "#888", fontSize: "0.9rem", fontStyle: "italic", marginBottom: "20px" }}>
              {transcript.compliance_note}
            </p>
            
            {transcript.transcript.entries.length === 0 ? (
              <p style={{ color: "#aaa", textAlign: "center" }}>No completed courses yet. Start your restoration journey!</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #333" }}>
                    <th style={{ textAlign: "left", padding: "10px", color: "#FFD700" }}>Course</th>
                    <th style={{ textAlign: "left", padding: "10px", color: "#FFD700" }}>Academic Label</th>
                    <th style={{ textAlign: "center", padding: "10px", color: "#FFD700" }}>Credits</th>
                  </tr>
                </thead>
                <tbody>
                  {transcript.transcript.entries.map((entry, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #222" }}>
                      <td style={{ padding: "10px", color: "#ddd" }}>{entry.ylc_course_name}</td>
                      <td style={{ padding: "10px", color: "#888", fontSize: "0.9rem" }}>{entry.academic_course_name}</td>
                      <td style={{ padding: "10px", color: "#90EE90", textAlign: "center" }}>{entry.credit_hours}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SpecialEdition;
